import React, { useEffect, useState } from 'react';
import { ChevronRight, MapPin } from 'lucide-react';
import { loadProjectNodes, CONTEXT_FIELD_ORDER } from '../lib/constructionCache';

/**
 * Compact, reusable construction-context breadcrumb.
 * Desktop: inline chips (Tower B › Floor 7 › Zone C › Masonry).
 * Mobile / long: shows first 2 chips + an expand toggle.
 * No context -> "Project level" / "Unassigned location".
 */
const ConstructionBreadcrumb = ({ projectId, context, fallback = 'Project level', testId = 'ctx-breadcrumb', className = '' }) => {
  const [byId, setById] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let active = true;
    loadProjectNodes(projectId).then((d) => { if (active) setById(d.byId); });
    return () => { active = false; };
  }, [projectId]);

  const hasCtx = context && Object.keys(context).length > 0;
  if (!hasCtx) {
    return <span className={`text-xs text-slate-400 ${className}`} data-testid={`${testId}-empty`}>{fallback}</span>;
  }

  const parts = CONTEXT_FIELD_ORDER
    .filter((f) => context[f])
    .map((f) => {
      const node = byId ? byId[context[f]] : null;
      return { field: f, id: context[f], name: node ? node.name : (byId ? '(archived)' : '…') };
    });

  if (parts.length === 0) {
    return <span className={`text-xs text-slate-400 ${className}`} data-testid={`${testId}-empty`}>{fallback}</span>;
  }

  const visible = expanded ? parts : parts.slice(0, 2);
  const hiddenCount = parts.length - visible.length;

  return (
    <span className={`inline-flex items-center flex-wrap gap-1 ${className}`} data-testid={testId}>
      <MapPin className="w-3 h-3 text-blue-500 shrink-0" />
      {visible.map((p, i) => (
        <span key={p.field} className="inline-flex items-center gap-1">
          <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-[11px] whitespace-nowrap">{p.name}</span>
          {i < visible.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300" />}
        </span>
      ))}
      {hiddenCount > 0 && (
        <button onClick={(e) => { e.stopPropagation(); setExpanded(true); }} className="text-[11px] text-blue-600 hover:underline" data-testid={`${testId}-expand`}>
          +{hiddenCount}
        </button>
      )}
    </span>
  );
};

export default ConstructionBreadcrumb;
